import { createMemo, createSignal } from 'solid-js'
import {
   isMetricId,
   METRIC_IDS,
   type MetricId as RegistryMetricId,
} from '@fasting-timer/shared'

import type { EffortBucketSize } from '../components/measurements/metricViewUtils'
import { DEFAULT_RANGE_FILTER_KEY, type RangeFilterKey } from '../utils/timeRange'
import { liveMeasurementEntries } from './measurementsStore'

/**
 * Identifiers for the metrics that can be shown in the measurements tab:
 * `weight` is the built-in metric backed by `weightStore`; the rest (sleep and the
 * exercise metrics) come from the shared registry and are backed by `measurementsStore`.
 */
export type MetricId = 'weight' | RegistryMetricId

const DEFAULT_METRIC_ID: MetricId = 'weight'
const SELECTED_METRIC_STORAGE_KEY = 'selectedMetric'
const ENABLED_METRICS_STORAGE_KEY = 'enabledMetrics'
const CHART_VALUE_STORAGE_KEY = 'measurementsChartValue'
const EFFORT_BUCKET_STORAGE_KEY = 'measurementsEffortBucket'
/** The effort chart defaults to months because the target is a monthly one. */
const DEFAULT_EFFORT_BUCKET: EffortBucketSize = 'month'

/** Which derived value the exercise timeline plots. */
type MeasurementsChartValue = 'pace' | 'speed'

/**
 * Normalizes a stored bucket size to a known one.
 *
 * @param value - The candidate size.
 * @returns The bucket size, falling back to the default.
 */
function normalizeBucketSize(value: unknown): EffortBucketSize {
   return value === 'week' || value === 'month' ? value : DEFAULT_EFFORT_BUCKET
}

/**
 * Normalizes a stored or requested metric id to a known one.
 *
 * @param value - The candidate id.
 * @returns The metric id, falling back to the default.
 */
function normalizeMetricId(value: unknown): MetricId {
   return value === 'weight' || isMetricId(value) ? value : DEFAULT_METRIC_ID
}

/**
 * Reads a JSON string array from local storage.
 *
 * @param key - The storage key.
 * @returns The strings, or an empty list when unset or unavailable.
 */
function readStoredStrings(key: string): string[] {
   try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]')
      return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : []
   } catch {
      return []
   }
}

/**
 * Writes a string into local storage, ignoring availability errors.
 *
 * @param key - The storage key.
 * @param value - The value.
 */
function writeStored(key: string, value: string) {
   try {
      localStorage.setItem(key, value)
   } catch {
      // Storage can be unavailable (private mode, quota); the selection still applies in memory.
   }
}

/**
 * Reads the enabled metric list; `weight` and `sleep` are always first and always enabled.
 *
 * @returns The enabled metric ids in display order.
 */
function loadEnabledMetrics(): MetricId[] {
   const stored = readStoredStrings(ENABLED_METRICS_STORAGE_KEY).filter(isMetricId)
   return [...new Set<MetricId>(['weight', 'sleep', ...stored])]
}

const [storedEnabledMetricIds, setStoredEnabledMetricIds] =
   createSignal<MetricId[]>(loadEnabledMetrics())

/**
 * Metrics shown in the picker: the ones enabled on this device plus any metric
 * that has entries (e.g. synced from another device), so synced data is never
 * hidden behind the "+" menu.
 */
const enabledMetricIds = createMemo<MetricId[]>(() => {
   const withEntries = liveMeasurementEntries().map(entry => entry.metricId)
   return [...new Set([...storedEnabledMetricIds(), ...withEntries])]
})

const [selectedMetricId, setSelectedMetricIdSignal] = createSignal<MetricId>(
   (() => {
      try {
         const stored = normalizeMetricId(
            localStorage.getItem(SELECTED_METRIC_STORAGE_KEY),
         )
         return enabledMetricIds().includes(stored) ? stored : DEFAULT_METRIC_ID
      } catch {
         return DEFAULT_METRIC_ID
      }
   })(),
)
const [measurementsRangeFilter, setMeasurementsRangeFilterSignal] =
   createSignal<RangeFilterKey>(DEFAULT_RANGE_FILTER_KEY)
const [measurementsGroup, setMeasurementsGroupSignal] = createSignal<number | null>(null)
const [measurementsPage, setMeasurementsPageSignal] = createSignal(0)
const [measurementsChartValue, setMeasurementsChartValueSignal] =
   createSignal<MeasurementsChartValue>(
      (() => {
         try {
            return localStorage.getItem(CHART_VALUE_STORAGE_KEY) === 'speed' ?
                  'speed'
               :  'pace'
         } catch {
            return 'pace'
         }
      })(),
   )

const [measurementsEffortBucket, setMeasurementsEffortBucketSignal] =
   createSignal<EffortBucketSize>(
      (() => {
         try {
            return normalizeBucketSize(localStorage.getItem(EFFORT_BUCKET_STORAGE_KEY))
         } catch {
            return DEFAULT_EFFORT_BUCKET
         }
      })(),
   )

/**
 * Sets the effort chart's bucket size and remembers it locally.
 *
 * @param size - The bucket size.
 */
export function setMeasurementsEffortBucket(size: EffortBucketSize) {
   const next = normalizeBucketSize(size)
   setMeasurementsEffortBucketSignal(next)
   writeStored(EFFORT_BUCKET_STORAGE_KEY, next)
}

/**
 * Selects the metric shown in the measurements tab and remembers it locally.
 *
 * @param value - The metric id to select.
 */
export function setSelectedMetricId(value: MetricId) {
   const next = normalizeMetricId(value)
   if (next !== selectedMetricId()) {
      setMeasurementsGroupSignal(null)
      setMeasurementsPageSignal(0)
   }
   setSelectedMetricIdSignal(next)
   writeStored(SELECTED_METRIC_STORAGE_KEY, next)
}

/**
 * Adds a registry metric to the picker and selects it.
 *
 * @param value - The registry metric id.
 */
export function enableMetric(value: RegistryMetricId) {
   if (!storedEnabledMetricIds().includes(value)) {
      const next = [...storedEnabledMetricIds(), value]
      setStoredEnabledMetricIds(next)
      writeStored(ENABLED_METRICS_STORAGE_KEY, JSON.stringify(next.filter(isMetricId)))
   }
   setSelectedMetricId(value)
}

/**
 * Returns the registry metrics not yet shown in the picker.
 *
 * @returns The available registry metric ids.
 */
export function getAvailableMetricIds(): RegistryMetricId[] {
   return METRIC_IDS.filter(id => !enabledMetricIds().includes(id))
}

/**
 * Sets the time-range filter for exercise metrics and resets pagination.
 *
 * @param key - The filter key.
 */
export function setMeasurementsRangeFilter(key: RangeFilterKey) {
   setMeasurementsRangeFilterSignal(key)
   setMeasurementsPageSignal(0)
}

/**
 * Sets the active distance group (null for all) and resets pagination.
 *
 * @param group - The snapped group value, or null.
 */
export function setMeasurementsGroup(group: number | null) {
   setMeasurementsGroupSignal(group)
   setMeasurementsPageSignal(0)
}

/**
 * Sets the entry-list page (clamped to a non-negative index).
 *
 * @param page - The zero-based page index.
 */
export function setMeasurementsPage(page: number) {
   setMeasurementsPageSignal(Math.max(0, page))
}

/**
 * Switches the timeline between pace and speed and remembers the choice locally.
 */
export function toggleMeasurementsChartValue() {
   const next: MeasurementsChartValue =
      measurementsChartValue() === 'pace' ? 'speed' : 'pace'
   setMeasurementsChartValueSignal(next)
   writeStored(CHART_VALUE_STORAGE_KEY, next)
}

export {
   enabledMetricIds,
   measurementsChartValue,
   measurementsEffortBucket,
   measurementsGroup,
   measurementsPage,
   measurementsRangeFilter,
   selectedMetricId,
}
